import React, { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Toolbar,
  FormControl,
  FormControlLabel,
  Radio,
  Checkbox,
  RadioGroup,
  FormLabel,
  FormGroup,
  Divider,
  Typography,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';

import ZerpaTable, { ColumnObject } from '../layout/table/ZerpaTable';
import NotificationBox from '../layout/NotificationBox';
import GenericDialog from '../GenericDialog';
import ContentBox from '../layout/ContentBox';
import {
  Survey,
  Trial,
  ReviewSubmissionData,
  ReviewSurvey,
  AssignedReviewSubmission,
  UserKeys,
  InstituteKeys,
} from '../../redux/types';
import ApiClient from '../../loopback/apiClient';
import { PrintOptions } from '../../loopback/apiClient/api';
import { convertToQid } from '../../utilities/stringHelper';

export type GoToReviewType = (
  assignmentId: number,
  enrollment: AssignedReviewSubmission['enrollment'],
  reviewedTrial: Trial,
  survey: Survey,
  quipUser: UserKeys,
  insitute: InstituteKeys,
  reviewSurvey: ReviewSurvey,
) => void;

type Props = {
  submissions: ReviewSubmissionData;
  trial: Trial & {
    survey: Survey;
  };
  assignmentId: number;
  goToReview: GoToReviewType;
  selectedEnrollment: number | '' | null;
  locale: string;
  token?: string;
};

const ReviewSubmissions: React.FC<Props> = ({
  submissions: reviewData,
  trial,
  selectedEnrollment,
  assignmentId,
  goToReview,
  token,
  locale,
}: Props) => {
  const [showPrintDialog, setShowPrintDialog] = useState<boolean>(false);
  const [printQuestionOrientation, setPrintQuestionOrientation] = useState<string>('normal');
  const [displayAnswerOnly, setDisplayAnswerOnly] = useState<boolean>(false);
  const [pageLayout, setPageLayout] = useState<string>('portrait');
  const [pageBreakPerSection, setPageBreakPerSection] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const printAllHandler = (): void => {
    const landscape = pageLayout === 'landscape';
    if (token) {
      const options: PrintOptions = {
        orientation: printQuestionOrientation,
        answerOnly: displayAnswerOnly,
        landscape,
        breakSection: pageBreakPerSection,
      };
      window.open(ApiClient.getPrintBulkSubmissionsUrl(trial.id, locale, token, options), '_blank');
    }
  };

  const onPrint = () => {
    printAllHandler();
    setShowPrintDialog(false);
  };

  const onSetPrintQuestionOrientation = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPrintQuestionOrientation((event.target as HTMLInputElement).value);
  };

  const onSetDisplayAnswerOnly = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = (event.target as HTMLInputElement).value;
    setDisplayAnswerOnly(selected === 'true');
  };

  const onSetPageLayout = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = (event.target as HTMLInputElement).value;
    setPageLayout(selected);
  };

  const onSetPageBreakPerSection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = (event.target as HTMLInputElement).checked;
    setPageBreakPerSection(selected);
  };

  const onRowClick = useCallback((rowData: NormalizedSubmissionListType[0]): void => {
    goToReview(
      assignmentId,
      rowData.enrollment,
      trial,
      trial.survey,
      rowData.quipUser,
      rowData.institute,
      reviewData.reviewSurvey,
    );
  }, [assignmentId, goToReview, trial, reviewData.reviewSurvey]);

  const submissions = reviewData.submissionsData;
  if (!submissions || !submissions.length) {
    return (
      <NotificationBox>
        <FormattedMessage id="review.unavailable" />
      </NotificationBox>
    );
  }

  const anonomyize = !!submissions.find((
    s: AssignedReviewSubmission,
  ) => s.enrollment.sampleset !== null);

  // Memoize this
  const normalizedSubmissionList = submissions
    .map(({
      enrollment, quipUser, institute,
    }, indx: number) => ({
      id: enrollment.id,
      sampleset: enrollment.sampleset,
      submission: enrollment.submission,
      createdAt: enrollment.createdAt,
      surname: quipUser.surname,
      firstname: quipUser.firstname,
      title: quipUser.title,
      city: institute.city,
      instituteName: institute.name,
      qId: convertToQid(quipUser.id, (institute.country || quipUser.userCountry)),
      quipUser,
      institute,
      enrollment,
      index: indx,
      groundTruthId: enrollment.groundTruthId,
    }));
  type NormalizedSubmissionListType = typeof normalizedSubmissionList;

  const zerpaColumn: ColumnObject[] = anonomyize ? [
    {
      keyName: 'sampleset',
      text: <FormattedMessage id="review.anonymizeHeader" />,
      sortable: true,
    },
  ] : [
    {
      keyName: 'qId',
      text: 'Q-ID',
      sortable: true,
    },
  ];
  zerpaColumn.push({
    keyName: 'createdAt',
    text: <FormattedMessage id="trialdetails.enrollDate" />,
    sortable: true,
    isDate: true,
  },
  {
    keyName: 'groundTruthId',
    text: <FormattedMessage id="groundtruthNo" />,
    sortable: true,
  });

  setTimeout(() => {
    // eslint-disable-next-line no-undef
    const elem = document.getElementById(`EnrollmentRow-${selectedEnrollment}`);
    if (elem) {
      // eslint-disable-next-line no-undef
      window.scrollTo(0, elem.getBoundingClientRect().top - 100);
    }
  }, 500);

  return (
    <>
      <Toolbar disableGutters>
        <Typography variant="h5" style={{ flexGrow: 1 }}><FormattedMessage id="review.submissionsHeadline" values={{ trial: trial.name }} /></Typography>
        <Button color="primary" variant="contained" startIcon={<PrintIcon />} onClick={() => setShowPrintDialog(true)}>
          <FormattedMessage id="review.printAll" />
        </Button>
      </Toolbar>
      <ZerpaTable<NormalizedSubmissionListType[0]>
        dense
        columns={zerpaColumn}
        keyIdentifier="index"
        tableRows={normalizedSubmissionList}
        rowClick={onRowClick}
      />
      <GenericDialog
        isOpen={showPrintDialog}
        closeButtonLabel={<FormattedMessage id="genericClose" />}
        onClose={() => setShowPrintDialog(false)}
        maxWidth="sm"
        actions={[
          <Button
            variant="contained"
            color="primary"
            key="confirm"
            onClick={onPrint}
          >
            <FormattedMessage id="confirm" />
          </Button>,
        ]}
      >
        <ContentBox title={<FormattedMessage id="review.printSettings" />} elevation={0}>
          <FormControl component="fieldset">
            <FormLabel component="legend"><FormattedMessage id="review.questionsLayout" /></FormLabel>
            <RadioGroup row aria-label="questionlayout" name="questionlayout" value={printQuestionOrientation} onChange={onSetPrintQuestionOrientation}>
              <FormControlLabel value="normal" control={<Radio />} label={<FormattedMessage id="review.printHorizontalQuestion" />} />
              <FormControlLabel value="switch" control={<Radio />} label={<FormattedMessage id="review.printVerticalQuestion" />} />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset">
            <FormLabel component="legend"><FormattedMessage id="review.displayOptions" /></FormLabel>
            <RadioGroup row aria-label="questionlayout" name="questionlayout" value={displayAnswerOnly} onChange={onSetDisplayAnswerOnly}>
              <FormControlLabel value={false} control={<Radio />} label={<FormattedMessage id="review.displayAllQuestions" />} />
              <FormControlLabel value control={<Radio />} label={<FormattedMessage id="review.displayAnsweredQuestions" />} />
            </RadioGroup>
          </FormControl>
          <Divider style={{ margin: '20px auto' }} variant="middle" light />
          <FormControl component="fieldset">
            <FormLabel component="legend"><FormattedMessage id="print.pageLayout" /></FormLabel>
            <RadioGroup row aria-label="pagelayout" name="pagelayout" value={pageLayout} onChange={onSetPageLayout}>
              <FormControlLabel value="portrait" control={<Radio />} label={<FormattedMessage id="print.portrait" />} />
              <FormControlLabel value="landscape" control={<Radio />} label={<FormattedMessage id="print.landscape" />} />
            </RadioGroup>
          </FormControl>
          <FormControl component="fieldset">
            <FormLabel component="legend"><FormattedMessage id="print.pagebreak" /></FormLabel>
            <FormGroup>
              <FormControlLabel control={<Checkbox checked={pageBreakPerSection} onChange={onSetPageBreakPerSection} />} label={<FormattedMessage id="review.sectionBreak" />} />
            </FormGroup>
          </FormControl>
        </ContentBox>
      </GenericDialog>
    </>
  );
};

export default ReviewSubmissions;
